/**
 * The terminal error screen: every fatal or blocking failure `main.ts` cannot recover from —
 * `router.start()` rejecting outright, or any in-app navigation (`safeNavigate`) whose route
 * handler threw, most commonly `mountRoomRoute`'s own multi-step boot (a failed
 * `/api/bootstrap`, a broken room snapshot, a hub that refuses to connect). By the time either
 * happens, `Router.render`'s own cleanup-before-render ordering has already torn down whatever
 * was on screen, so without a real way out the tab goes blank with nothing left to click.
 *
 * The owner got stuck on exactly this once: a bootstrap failure landed on a bare error message
 * with no escape but typing `/portal` into the address bar by hand to reach a sign-out button.
 * Every fatal or blocking screen in this app needs its own way out — this is the one place that
 * builds it, so `main.ts`'s two call sites (`router.start().catch()`, `safeNavigate`) cannot
 * drift into offering different escapes.
 *
 * Pulled out of `main.ts` into its own module specifically so it is unit-testable — `main.ts`
 * itself has no test harness (real top-level side effects, a live `Router`, a live canvas), so
 * this had no way to be pinned red-then-green while it lived there.
 */
export function showFatalErrorScreen(host: HTMLElement, message: string, onSignOut: () => void): void {
  host.innerHTML = '';

  const panel = document.createElement('div');
  panel.className = 'splash';
  panel.innerHTML = `
    <div class="splash-panel">
      <h1>tunet</h1>
      <p class="splash-error"></p>
      <p class="splash-foot">Is the backend running? Try <b>npm start</b>, then reload.</p>
      <div class="splash-account-actions">
        <a class="splash-link" href="/portal">Portal</a>
        <button type="button" class="splash-link" data-signout>Sign out</button>
      </div>
    </div>
  `;

  // Built as a DOM node and set through textContent, not interpolated into the template
  // string above — `message` can carry a raw server error, and must never become markup
  // (the same reasoning `Splash`'s own `escapeHtml` exists for).
  panel.querySelector('.splash-error')!.textContent = message;
  panel.querySelector<HTMLButtonElement>('[data-signout]')!.addEventListener('click', onSignOut);

  host.appendChild(panel);
}
